/**
 * CaseStudiesIndexPage — /case-studies index.
 *
 * Rich card grid over the build-time manifest: a wide featured card (the
 * study flagged `featured`, shown on the unfiltered view), a type filter
 * rail (derived from the data, so new types appear automatically), and a
 * responsive grid of CaseStudyCards. All studies render in the prerendered
 * HTML — the filter only hides cards client-side, so crawlers always see
 * the full collection.
 */
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { MotionConfig } from 'motion/react';
import SchemaJsonLd from '../components/primitives/SchemaJsonLd.jsx';
import SectionHead from '../components/ui/SectionHead.jsx';
import CaseStudyCard from '../components/primitives/CaseStudyCard.jsx';
import { caseStudies } from '../content/case-studies-manifest.js';
import { caseStudiesIndex } from '../content/caseStudies.js';

const caseStudiesIndexSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'CollectionPage',
      '@id': 'https://artificersai.com/case-studies#collection',
      name: 'Case Studies',
      description: caseStudiesIndex.tagline,
      url: 'https://artificersai.com/case-studies',
      inLanguage: 'en-AU',
      publisher: {
        '@type': 'Organization',
        name: "The Artificer's AI",
        url: 'https://artificersai.com',
      },
    },
    {
      '@type': 'ItemList',
      '@id': 'https://artificersai.com/case-studies#itemList',
      itemListOrder: 'https://schema.org/ItemListOrderDescending',
      numberOfItems: caseStudies.length,
      itemListElement: caseStudies.map((c, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        url: c.canonical || `https://artificersai.com/case-studies/${c.slug}`,
        name: c.title,
      })),
    },
  ],
};

function FilterPill({ active, onClick, children }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={
        'font-mono text-mono-md px-4 py-2 rounded-full border cursor-pointer ' +
        'transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-bright/60 ' +
        (active
          ? 'bg-brand-blue-bright/[.12] border-brand-blue-bright/50 text-ink-50'
          : 'bg-white/[.02] border-white/[.08] text-ink-300 hover:text-ink-50 hover:border-white/[.2]')
      }
    >
      {children}
    </button>
  );
}

export default function CaseStudiesIndexPage() {
  const [activeType, setActiveType] = useState('all');

  const types = useMemo(
    () => [...new Set(caseStudies.map((c) => c.type).filter(Boolean))],
    []
  );

  const showFilters = types.length > 1;
  const unfiltered = activeType === 'all';
  const visible = unfiltered ? caseStudies : caseStudies.filter((c) => c.type === activeType);

  // Featured treatment only applies on the unfiltered view; under a type
  // filter the featured study drops back into the grid like any other.
  const featured = unfiltered ? visible.find((c) => c.featured) : undefined;
  const gridEntries = featured ? visible.filter((c) => c !== featured) : visible;

  return (
    <>
      <Helmet>
        <title>Case Studies — The Artificer's AI</title>
        <meta name="description" content={caseStudiesIndex.tagline} />
        <link rel="canonical" href="https://artificersai.com/case-studies" />
        <meta property="og:title" content="Case Studies — The Artificer's AI" />
        <meta property="og:description" content={caseStudiesIndex.tagline} />
        <meta property="og:url" content="https://artificersai.com/case-studies" />
      </Helmet>

      <SchemaJsonLd data={caseStudiesIndexSchema} />

      <section className="section-pad bg-surface text-ink-50">
        <div className="container-page">
          <SectionHead
            eyebrow={caseStudiesIndex.eyebrow}
            heading={caseStudiesIndex.heading}
            description={caseStudiesIndex.tagline}
          />

          {caseStudies.length === 0 ? (
            <p className="text-body text-ink-300">{caseStudiesIndex.emptyState}</p>
          ) : (
            <MotionConfig reducedMotion="user">
              {showFilters && (
                <div className="flex flex-wrap items-center gap-3 mb-10">
                  <FilterPill active={unfiltered} onClick={() => setActiveType('all')}>
                    {caseStudiesIndex.filterAll}
                  </FilterPill>
                  {types.map((t) => (
                    <FilterPill key={t} active={activeType === t} onClick={() => setActiveType(t)}>
                      {caseStudiesIndex.typeLabels[t] ?? t}
                    </FilterPill>
                  ))}
                  <span className="font-mono text-mono-xs tracking-[0.08em] uppercase text-ink-300 ml-auto">
                    {visible.length} {visible.length === 1 ? 'study' : 'studies'}
                  </span>
                </div>
              )}

              <div className="flex flex-col gap-4 md:gap-5">
                {featured && <CaseStudyCard entry={featured} featured />}

                {gridEntries.length > 0 && (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                    {gridEntries.map((c, i) => (
                      <CaseStudyCard key={c.slug} entry={c} delay={0.06 * (i % 3)} />
                    ))}
                  </div>
                )}
              </div>
            </MotionConfig>
          )}
        </div>
      </section>
    </>
  );
}
